import { httpRequest, Urls, ApiResult } from './Shared';
import { CollectionType, ReportFrequency, VirtualToursEnabled } from '../utilities/Enums';
import { IdVerificationTypes } from './IdVerification';

// Paths

const PROPERTY_INTEGRATION_URL = `${Urls.BASE_URL}/PropertyIntegrations`;
const PROPERTY_URL = `${Urls.BASE_URL}/Properties`;

// Enums
export enum AccessType {
    MOBILE = 1,
    PINCODE = 2,
    BOTH = MOBILE | PINCODE
}

// Interfaces

export interface OnCellPropertyConfiguration {
    onCellProjectId: number;
    finalStep: string;
    signinCompletesTour: boolean;
    virtualToursEnabled: VirtualToursEnabled;
    collectionId: number;
    collectionType: CollectionType;
}

export interface DoorKingPropertyConfiguration {
    deviceId: string;
}

export interface BmxPropertyConfiguration {
    buildingId: string;
    tenantId: string;
    email: string;
}

export interface LatchAdvPropertyConfiguration {
    buildingId: string;
    id: number;
}

export interface GeoKeyPropertyPropertyConfiguration {
    geokeyPropertyId: string;
    units: Unit[] | undefined;
}

export interface BMXTenant {
    tenantId: string;
    buildingName: string;
    buildingAddress: string;
    buildingId: string;
    unitId: string;
}

export interface LatchDoorsResponse {
    nextPageToken: string;
    data: LatchDoors[];
}

export interface LatchDoors {
    doorUUid: string;
    doorName: string;
    doorType: string;
    buildingId: string;
    buildingName: string;
}

export interface StratisPropertyConfiguration {
    stratisPropertyId: string;
    suppressButterflyMX: boolean;
    usesBrivo: boolean;
    stratisAccessType: AccessType;
    isYaleEnabled: boolean;
}

export interface Unit {
    // unitId: string;
    name: string;
    remoteUnitId: string;
}

export interface SmartRentConfiguration {
    smartRentPropertyId: string;
    units: Unit[] | undefined;
}

export interface EntrataPropertyConfiguration {
    useForAvailability: boolean;
    suppressTourForwarding: boolean;
    enableSelfGuided: boolean;
    subdomain: string;
    propertyId: string;
    username: string;
    password: string;
    // used to display properly on frontend (not used on backend)
    editInProgress?: boolean;
}

export interface UnitMapPropertyConfiguration {
    unitMapId: string;
}

export interface Lead2LeasePropertyConfiguration {
    lead2LeasePropertyId: string;
}

export interface KnockPropertyConfiguration {
    knockCommunityId: string;
    useForAvailability: boolean;
    unitName: string;
    companyId: string;
}

export interface FunnelPropertyConfiguration {
    funnelCommunityId: string;
    useForAvailability: boolean;
    sendFloorPlan: boolean;
}

export interface HylyPropertyConfiguration {
    hylyCommunityId: string;
    useForAvailability: boolean;
}

export interface IntelligentLeadPropertyConfiguration {
    emailDestination: string;
}

export interface AmazonKeyConfiguration {
}

export interface AnyoneHomePropertyConfiguration {
    emailDestination: string;
    accountId?: string;
}

export interface RentCafePropertyConfiguration {
    apiToken: string;
    companyCode: string;
    propertyCode: string;
    propertyId: number;
    agent: string;
    useRentCafeCalendar: boolean;
}

export interface YardiPropertyConfiguration {
    username: string;
    password: string;
    serverName: string;
    database: string;
    propertyId: string;
    webServiceUrl: string;
    modelUnitName: string;
    sendModelUnitNameWithGuestCard: boolean;
    modelUnitTypeId: string;
    firstContactEventReason: string;
    agent: string;
    enableMultiPropertyConfig: boolean;
    propertyIds: any;
 }

export interface OneSitePropertyConfiguration {
    pmcId: string;
    siteId: string;
    unitDescription: string;
}

export interface MasterLockConfiguration {
    masterLocks: MasterLockIndividualLock[];
}

export interface MasterLockPropertyConfiguration {
    sharingCode: string;
    notify: boolean;
    emailList: string;
}

export interface AdvancedConfiguration {
    onCellConfiguration?: OnCellPropertyConfiguration;
    doorKingConfiguration?: DoorKingPropertyConfiguration;
    bMXConfiguration?: BmxPropertyConfiguration;
    stratisConfiguration?: StratisPropertyConfiguration;
    smartRentConfiguration?: SmartRentConfiguration;
    entrataConfiguration?: EntrataPropertyConfiguration;
    lead2LeaseConfiguration?: Lead2LeasePropertyConfiguration;
    knockConfiguration?: KnockPropertyConfiguration;
    funnelConfiguration?: FunnelPropertyConfiguration;
    hylyConfiguration?: HylyPropertyConfiguration;
    intelligentLeadConfiguration?: IntelligentLeadPropertyConfiguration;
    anyoneHomeConfiguration?: AnyoneHomePropertyConfiguration;
    rentCafeConfiguration?: RentCafePropertyConfiguration;
    brivoPropertyConfiguration?: BrivoPropertyConfiguration;
    brivoGroupIds?: BrivoGroupIds;
    brivoSiteIds?: BrivoSiteIds;
    igloohomePropertyConfiguration?: IgloohomePropertyConfiguration;
    latchConfiguration?: LatchPropertyConfiguration;
    geokeyConfiguration?: GeoKeyPropertyPropertyConfiguration;
    latchAdvPropertyConfiguration?: LatchAdvPropertyConfiguration;
    yardiConfiguration?: YardiPropertyConfiguration;
    oneSiteConfiguration?: OneSitePropertyConfiguration;
    masterLockConfiguration?: MasterLockConfiguration;
    masterLockPropertyConfiguration?: MasterLockPropertyConfiguration;
    unitMapConfiguration?: UnitMapPropertyConfiguration;
    amazonKeyConfiguration?: AmazonKeyConfiguration;
    keyCafePropertyConfiguration?: KeyCafePropertyConfiguration;
    keyCafePropertyKeys?: any;
    reportFrequency: ReportFrequency;
    remoteLockGroupName?: string;
    igloohomeLockName?: string;
    emailAccessInstructions?: string;
    manualCodesEnabled: boolean;
    textAccessInstructions?: string;
    customFollowUp?: string;
    emailConfirmationInstructions?: string;
    textConfirmationInstructions?: string;
    unitInstructions?: string;
    defaultForwardedSource?: string;
    cancellationDate?: string;
    idVerificationType: IdVerificationTypes;
}

export interface EntrataProperty {
    propertyId: string;
    name: string;
}

export interface EntrataRedemption {
    accessToken: string;
    subdomain: string;
    properties: EntrataProperty[];
    userId: number;
    companyName: string;
    cid: number;
}

export interface EntrataFullPropertyConfiguration {
    accessToken: string;
    entrataPropertyId: string;
    subdomain: string;
    userId: number;
}

export interface RemoteLockResources {
    groups: RemoteLockGroup[];
    locks: AlgorithmicLock[];
}

export interface RemoteLockResourceSelection {
    groupId: string | undefined;
    algorithmicLockId: string | undefined;
    name: string;
}

export interface RemoteLockGroup {
    type: string;
    attributes: RemoteLockGroupAttribute;
    id: string;
    links: RemoteLockGroupDataLinks;
}

export interface AlgorithmicLock {
    type: string;
    attributes: RemoteLockGroupAttribute;
    id: string;
    links: RemoteLockGroupDataLinks;
}

export interface RemoteLockGroupAttribute {
    name: string;
    createdAt: string;
    updatedAt: string;
}

export interface RemoteLockGroupDataLinks {
    self: string;
}

export interface RemoteLockRedemption {
    propertyId: number;
    redeemCode: string;
}

export interface IgloohomePropertyConfiguration {
    apiKey: string;
    igloohomeLocks: IgloohomeLock[];
}

export interface IgloohomeLock {
    lockName: string;
    lockUniqueId: string;
    id?: number;
    displayName: string;
}

export interface IgloohomeLockFromIgloohomeService {
    createdAt: string;
    id: string;
    name: string;
    displayName: string;
}

export interface IgloohomeLockSelection extends IgloohomeLock {
    apiKey: string;
}

export interface LatchPropertyConfiguration {
    latchKeys?: LatchKey[];
}

export interface LatchKey {
    keyId: string;
    type: string;
}

export interface MasterLockIndividualLock {
    deviceIdentifier: string;
    lockName: string;
    displayName: string;
    isEnabled: boolean;
}

export interface MasterLockActivation extends MasterLockIndividualLock {
    activationCode: string;
}

export enum CodeStatus {
    Active = 0,
    InUse = 1,
    Used = 2,
    Removed = 3
}

export interface ManualAccess {
    id: number;
    code: string;
    codeStatus: CodeStatus;
}

export interface ManualAccessPropertyConfiguration {
    backupCode?: ManualAccess;
    codes?: ManualAccess[];
    name?: string;
}

export interface KeyCafePropertyConfiguration {
     Id: number; 
     keyCafeLocationId: string;
     keyCafeKeyGroupId: string;
     notify: boolean;
     emailList: string;
}

export interface BrivoPropertyConfiguration {
    clientId: string;
    clinetSecret: string;
    siteIds: number[];
    apiKey: string;
    groupId: number;
    verified: boolean;
}

export interface keyCafeKeyGroups {
    id: number,
    localizedName: any,
    system: boolean,
    name: string,
    type: string,
    description: string
}

export interface keyCafeKeyLocations {
    id: number,
    localizedName: any,
    system: boolean,
    name: string,
    type: string,
    description: string
}

export interface BrivoToken {
    propertyId: number;
    authcode: string;
}

export interface BrivoGroupIds extends BrivoPropertyConfiguration {
    id: number;
    name: string;
}

export interface BrivoSiteIds extends BrivoPropertyConfiguration {
    id: number;
    name: string;
}
// Actions

export const getAdvancedConfiguration = (propertyId: number) =>
    httpRequest<AdvancedConfiguration>(`${PROPERTY_INTEGRATION_URL}/${propertyId}`, 'get');

export const fetchKeyCafeCompanies = () =>
    httpRequest<AdvancedConfiguration>(`${PROPERTY_INTEGRATION_URL}/keycafe/companies`, 'get');

export const fetchPropertyKeys = (propertyId: number) =>
    httpRequest<any>(`${PROPERTY_INTEGRATION_URL}/keycafe/GetPropertyKeys/${propertyId}`, 'get');

export const fetchKeyCafeLocations = (groupId: string) =>
    httpRequest<any>(`${PROPERTY_INTEGRATION_URL}/keycafe/locations/${groupId}`, 'get');

export const fetchKeyCafeKeys = (groupId: string) =>
    httpRequest<any>(`${PROPERTY_INTEGRATION_URL}/keycafe/${groupId}/keys`, 'get');

export const fetchKeyCafeKeyGroups = () =>
    httpRequest<keyCafeKeyGroups[]>(`${PROPERTY_INTEGRATION_URL}/keycafe/keygroups`, 'get');

export const updateAdvancedConfiguration = (
    propertyId: number,
    advancedConfiguration: AdvancedConfiguration
) => httpRequest<{}>(`${PROPERTY_INTEGRATION_URL}/${propertyId}`, 'put', advancedConfiguration);

export const redeemEntrataCode = (redeemCode: string) =>
    httpRequest<EntrataRedemption>(
        `${PROPERTY_INTEGRATION_URL}/RedeemEntrataCode`,
        'post',
        redeemCode
    );

export const connectEntrataProperty = (
    propertyId: number,
    entrataConfiguration: EntrataFullPropertyConfiguration
) =>
    httpRequest<{}>(
        `${PROPERTY_INTEGRATION_URL}/${propertyId}/ConnectEntrataProperty`,
        'post',
        entrataConfiguration
    );

export const redeemRemoteLockCode = (redemption: RemoteLockRedemption) =>
    httpRequest<RemoteLockResources>(
        `${PROPERTY_INTEGRATION_URL}/${redemption.propertyId}/RemoteLockCode`,
        'post',
        redemption.redeemCode
    );

export const setRemoteLockResourceForProperty = (
    propertyId: number,
    resourceSelection: RemoteLockResourceSelection
) =>
    httpRequest<{}>(
        `${PROPERTY_INTEGRATION_URL}/${propertyId}/RemoteLockResource`,
        'post',
        resourceSelection
    );

export const removeRemoteLockIntegration = (propertyId: number): Promise<ApiResult<undefined>> => {
    return httpRequest<undefined>(
        `${PROPERTY_INTEGRATION_URL}/${propertyId}/RemoveRemoteLockIntegration`,
        'post'
    );
};

export const getIgloohomeLocks = (apiKey: string) =>
    httpRequest<IgloohomeLockFromIgloohomeService[]>(
        `${PROPERTY_INTEGRATION_URL}/GetIgloohomeLocks/${apiKey}`
    );

export const getIgloohomeLocksByPropertyId = (propertyId: number) =>
    httpRequest<IgloohomeLock[]>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/igloohome`);

export const createIgloohomePropertyConfiguration = (propertyId: number, apiKey: string) =>
    httpRequest<{}>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/igloohome`, 'post', apiKey);

export const deleteIgloohomePropertyConfiguration = (propertyId: number) =>
    httpRequest<{}>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/igloohome`, 'delete');

export const addIgloohomeLock = (propertyId: number, lockInfo: IgloohomeLock) =>
    httpRequest<{}>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/igloohome/locks`, 'post', lockInfo);

export const selectIgloohomeLock = (propertyId: number, selection: IgloohomeLockSelection) =>
    httpRequest<{}>(
        `${PROPERTY_INTEGRATION_URL}/${propertyId}/SelectIgloohomeLock`,
        'post',
        selection
    );

export const deleteIgloohomeLock = (propertyId: number, lockId: string) =>
    httpRequest<{}>(
        `${PROPERTY_INTEGRATION_URL}/${propertyId}/igloohome/locks/${lockId}`,
        'delete'
    );

// Latch

export const addLatchKey = (propertyId: number, keyId: string, type: string) =>
    httpRequest<{}>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/AddLatchKey`, 'post', { keyId, type });

export const deleteLatchKey = (propertyId: number, keyId: string) =>
    httpRequest<{}>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/keys/${keyId}`, 'delete');

export const deleteLatchConfiguration = (propertyId: number) =>
    httpRequest<{}>(`${PROPERTY_INTEGRATION_URL}/${propertyId}`, 'delete');

// Master Lock

export const addMasterLock = (propertyId: number, lockInfo: MasterLockActivation) =>
    httpRequest<{}>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/addMasterLock`, 'post', lockInfo);

export const deleteMasterLock = (propertyId: number, deviceIdentifier: string) =>
    httpRequest<{}>(
        `${PROPERTY_INTEGRATION_URL}/${propertyId}/masterLocks/${deviceIdentifier}`,
        'delete'
    );

// BMX 
//const bmxUrl = (propertyId: number) => `${PROPERTY_INTEGRATION_URL}/bmx/tenants`;
export const getTenants = () => httpRequest<BMXTenant[]>(`${PROPERTY_INTEGRATION_URL}/bmx/tenants`);
// Latch 
//const bmxUrl = (propertyId: number) => `${PROPERTY_INTEGRATION_URL}/bmx/tenants`;
export const getDoors = (propertyId: number) => httpRequest<LatchDoors[]>(`${Urls.BASE_URL}/Properties/${propertyId}/getLatchDoorDetails`);

export const deleteMasterLockConfiguration = (propertyId: number) =>
    httpRequest<{}>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/masterLock`, 'delete');

// Manual Codes

const manualCodeUrl = (propertyId: number) =>
    `${PROPERTY_INTEGRATION_URL}/${propertyId}/ManualCodes`;

export const getManualAccess = (propertyId: number) =>
    httpRequest<ManualAccessPropertyConfiguration>(manualCodeUrl(propertyId));

export const addManualAccess = (propertyId: number, code: string, name: string) =>
    httpRequest<ManualAccessPropertyConfiguration>(manualCodeUrl(propertyId), 'POST', { code: code, name: name });

export const updateManualConfiguration = (propertyId: number, code: string, name: string) =>
    httpRequest<ManualAccessPropertyConfiguration>(manualCodeUrl(propertyId), 'PUT', { code: code, name: name });

export const revokeManualAccesses = (propertyId: number, revokedCodeIds: number[]) =>
    httpRequest<ManualAccessPropertyConfiguration>(
        manualCodeUrl(propertyId),
        'DELETE',
        revokedCodeIds
    );

export const disableManualAccess = (propertyId: number) =>
    httpRequest<ManualAccessPropertyConfiguration>(
        `${manualCodeUrl(propertyId)}/disable`,
        'DELETE'
    );

export const addSmartRentRemoteKey = (propertyId: number, smartRentPropertyId: string, newunit: any) =>
    httpRequest<Unit>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/smartrent/${smartRentPropertyId}?RemoteUnitId=${newunit.remoteUnitId}&Name=${newunit.name}`, 'POST', newunit
    );

export const deleteSmartRentKey = (propertyId: number, smartRentPropertyId: string, remoteUnitId: string) =>
    httpRequest<Unit>(`${PROPERTY_INTEGRATION_URL}/${propertyId}/smartrent/${smartRentPropertyId}/key/${remoteUnitId}`, 'DELETE');

export const deleteSmartRentIntegration = (propertyId: number) => httpRequest(`${PROPERTY_INTEGRATION_URL}/${propertyId}/smartrent`, 'DELETE');

const BRIVOTOKENURL = (propertyId: number, authcode: string) =>
`${PROPERTY_INTEGRATION_URL}/${propertyId}/brivo/token/${authcode}`;

export const addBrivoToken = (brivoToken: BrivoToken): Promise<ApiResult<BrivoToken>> => {
    const url = BRIVOTOKENURL(brivoToken.propertyId, brivoToken.authcode);
    console.log('Request URL:', url);
    return httpRequest<BrivoToken>(
        url,
       // BRIVOTOKENURL(brivoToken.propertyId, brivoToken.authcode), 
        'post', 
        brivoToken

    );
};

export const getAllBrivoGroups  = (propertyId: number): Promise<ApiResult<BrivoGroupIds[]>> => {
    return httpRequest<BrivoGroupIds[]>(`${PROPERTY_URL}/brivo/getAllGroups/${propertyId}`, 'get');
};
 
export const getAllBrivoSites  = (propertyId: number): Promise<ApiResult<BrivoSiteIds[]>> => {
    return httpRequest<BrivoSiteIds[]>(`${PROPERTY_URL}/brivo/getAllSites/${propertyId}`, 'get');
};




// WEBPACK FOOTER //
// ./src/api/PropertyIntegration.ts